// File System Access API implementation of the folder-watching provider.
//
// This is the ONLY module that talks to `showDirectoryPicker`/`FileSystemDirectoryHandle`
// directly. `useLocalAutoSync` only calls the methods below, so a future native
// (e.g. Android/Capacitor) build can supply an alternate provider with the same
// method signatures without touching the orchestration logic.
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'heic', 'heif']);

export class ScanCancelledError extends Error {
  constructor() {
    super('Folder scan cancelled.');
    this.name = 'ScanCancelledError';
  }
}

function extOf(name) {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i + 1).toLowerCase();
}

// `onDiscover(count)` fires as each image file is found during the recursive
// walk itself (before any getFile() calls) - the only progress signal
// available at this stage, since the total isn't known until the walk
// finishes. `signal`, if given, aborts the walk (throwing ScanCancelledError)
// as soon as as it's flagged, checked between every directory entry.
async function walk(dirHandle, prefix, out, { onDiscover, signal } = {}) {
  if (signal?.aborted) throw new ScanCancelledError();
  for await (const entry of dirHandle.values()) {
    if (signal?.aborted) throw new ScanCancelledError();
    if (entry.kind === 'file') {
      if (!IMAGE_EXTENSIONS.has(extOf(entry.name))) continue;
      const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
      out.push({ relativePath, handle: entry });
      onDiscover?.(out.length);
    } else if (entry.kind === 'directory') {
      await walk(entry, prefix ? `${prefix}/${entry.name}` : entry.name, out, { onDiscover, signal });
    }
  }
}

export const webFileSystemAccessProvider = {
  isSupported() {
    return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
  },

  async pickDirectory() {
    try {
      return await window.showDirectoryPicker({ mode: 'read' });
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      throw err;
    }
  },

  async queryPermission(dirHandle) {
    if (!dirHandle?.queryPermission) return 'denied';
    return dirHandle.queryPermission({ mode: 'read' });
  },

  async requestPermission(dirHandle) {
    if (!dirHandle?.requestPermission) return 'denied';
    return dirHandle.requestPermission({ mode: 'read' });
  },

  // Recursively lists image files under dirHandle. Returns entries identified
  // by relativePath alone (change-detection is "have we seen this path
  // before", not "has its content changed" - an edited-in-place existing
  // file is not treated as new) plus a lazy getFile() so callers only read
  // the actual File when uploading.
  //
  // `onProgress({ phase: 'scanning', completed, total: null })` fires as each
  // file is discovered during the walk - `total` is always null since the
  // walk doesn't know the count upfront. Deliberately does NOT open/read any
  // file during the scan itself (that used to happen here, to resolve a
  // size/lastModified fingerprint) - for a folder with thousands of files,
  // and especially for the periodic re-scan of an already-armed folder,
  // walking the directory tree is cheap but opening every file is not, and
  // relativePath alone is all "is this new" needs.
  // `signal` (an AbortSignal), if given and aborted, throws ScanCancelledError
  // as soon as the in-flight walk notices, letting a caller cancel a scan of
  // a very large folder instead of waiting it out.
  async listImageFiles(dirHandle, { onProgress, signal } = {}) {
    const raw = [];
    await walk(dirHandle, '', raw, {
      onDiscover: (count) => onProgress?.({ phase: 'scanning', completed: count, total: null }),
      signal,
    });
    return raw.map(({ relativePath, handle }) => ({ relativePath, getFile: () => handle.getFile() }));
  },

  // Re-resolves a File from a relativePath persisted earlier (pending-queue
  // entries only store plain strings/numbers so they can be IndexedDB-cloned;
  // the live FileSystemFileHandle/getFile() closure is looked up again here at
  // submit time). Throws if the path no longer exists (file moved/deleted).
  async resolveFile(dirHandle, relativePath) {
    const parts = relativePath.split('/').filter(Boolean);
    let dir = dirHandle;
    for (let i = 0; i < parts.length - 1; i += 1) {
      dir = await dir.getDirectoryHandle(parts[i]);
    }
    const fileHandle = await dir.getFileHandle(parts[parts.length - 1]);
    return fileHandle.getFile();
  },
};
